import { Link } from 'react-router-dom';
import {
  Bell,
  RefreshCw,
  Loader2,
  Radio,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  useDeliveryGatesQuery,
  useNotificationChannelsQuery,
  useNotificationProvidersQuery,
  useNotificationsOverviewQuery,
  useProvidersHealthQuery,
} from '@/client/api/notifications-infra/notifications.query';
import { ProviderHealth, ProviderRow } from '@/client/types/notifications-infra';
import {
  CapabilityChips,
  DELIVERY_BASE,
  DeliveryAccessDenied,
  DeliveryPageShell,
  GatesBanner,
  HealthStatus,
  MESSAGING_TEMPLATES_HREF,
  MESSAGING_TYPES_HREF,
  formatChannel,
  formatProviderName,
  normalizeGates,
  pickMappingHealth,
  providerKey,
  resolveHealth,
  routedCountryCount,
} from '@/client/components/admin/NotificationsInfra/deliveryUi';

const DeliveryOverviewPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: `${DELIVERY_BASE}/delivery` },
    { label: 'Notifications' },
    { label: 'Delivery' },
  ]);

  const viewPerm = useHasPermission('messaging.view');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const overviewQ = useNotificationsOverviewQuery(canView);
  const gatesQ = useDeliveryGatesQuery(canView);
  const healthQ = useProvidersHealthQuery(undefined, canView);

  const overviewEmpty =
    !overviewQ.isLoading &&
    !(overviewQ.data?.channels?.length || overviewQ.data?.providers?.length);
  const channelsQ = useNotificationChannelsQuery(canView && overviewEmpty);
  const providersQ = useNotificationProvidersQuery(undefined, canView && overviewEmpty);

  if (permLoading) {
    return (
      <DeliveryPageShell loading icon={null} title="Delivery" description="">
        {null}
      </DeliveryPageShell>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <DeliveryAccessDenied />
        </div>
      </div>
    );
  }

  const channels = overviewQ.data?.channels?.length
    ? overviewQ.data.channels
    : channelsQ.data?.channels ?? [];
  const providers: ProviderRow[] = overviewQ.data?.providers?.length
    ? overviewQ.data.providers
    : providersQ.data?.providers ?? [];
  const gates = normalizeGates(gatesQ.data).length
    ? normalizeGates(gatesQ.data)
    : normalizeGates(overviewQ.data);
  const mapping = pickMappingHealth(overviewQ.data);
  const healthByProvider = new Map<string, ProviderHealth>();
  for (const h of healthQ.data?.providers ?? []) {
    const key = providerKey(h);
    if (key) healthByProvider.set(key, h);
  }
  const proxyUnavailable = !!healthQ.data?.unavailable;
  const problems = mapping?.with_problems ?? 0;

  const loading = overviewQ.isLoading && channelsQ.isLoading;
  const error = overviewQ.isError && channelsQ.isError ? overviewQ.error : overviewQ.error;

  return (
    <DeliveryPageShell
      icon={<Bell className="h-4 w-4" />}
      title="Delivery"
      description="Provider layer: which vendor carries which channel, kill-switches, and whether templates can actually deliver."
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            overviewQ.refetch();
            gatesQ.refetch();
            healthQ.refetch();
            if (overviewEmpty) {
              channelsQ.refetch();
              providersQ.refetch();
            }
          }}
          disabled={overviewQ.isFetching || healthQ.isFetching}
          className="gap-2"
        >
          {overviewQ.isFetching || healthQ.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </Button>
      }
    >
      <GatesBanner gates={gates} loading={gatesQ.isLoading} />

      {error && overviewQ.isError && channels.length === 0 && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
          {getApiErrorMessage(error, 'Failed to load delivery overview.')}
        </div>
      )}

      {proxyUnavailable && (
        <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <Radio className="mt-0.5 h-4 w-4 shrink-0" />
          Live vendor health is unavailable (proxy), not a vendor outage. Balance will show as
          unavailable until ff-api-external responds.
        </div>
      )}

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Channels live"
          value={
            loading ? '…' : String(channels.filter((c) => c.live !== false).length)
          }
        />
        <Stat label="Providers" value={loading ? '…' : String(providers.length)} />
        <Stat
          label="Kill-switches"
          value={gatesQ.isLoading ? '…' : String(gates.filter((g) => g.active !== false && g.active !== 0).length)}
          tone={
            gates.filter((g) => g.active !== false && g.active !== 0).length > 0
              ? 'warn'
              : 'ok'
          }
        />
        <Link
          to={`${DELIVERY_BASE}/reconciliation`}
          className={`rounded-xl border p-4 shadow-sm transition-colors duration-150 ${
            problems > 0
              ? 'border-red-200 bg-red-50 hover:bg-red-100/70'
              : 'border-gray-100 bg-white hover:bg-gray-50'
          }`}
        >
          <p className="text-xs font-medium text-gray-500">Mapping health</p>
          {overviewQ.isLoading ? (
            <Skeleton className="mt-2 h-7 w-20" />
          ) : mapping ? (
            <>
              <p
                className={`mt-1 text-2xl font-semibold tabular-nums ${
                  problems > 0 ? 'text-red-800' : 'text-gray-900'
                }`}
              >
                {problems > 0 ? `${problems} broken` : 'All clear'}
              </p>
              <p className="mt-0.5 text-xs text-gray-500">
                {mapping.deliverable ?? 0}/{mapping.types_checked ?? 0} types deliverable
              </p>
            </>
          ) : (
            <p className="mt-1 text-sm text-gray-500">Open reconciliation →</p>
          )}
        </Link>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Channels</h2>
        </div>
        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {channels.map((ch) => {
              const live = ch.live !== false;
              return (
                <div
                  key={ch.channel}
                  className={`rounded-xl border bg-white p-4 shadow-sm ${
                    live ? 'border-gray-100' : 'border-dashed border-gray-200 bg-gray-50/80'
                  }`}
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-gray-900">{formatChannel(String(ch.channel))}</h3>
                    {live ? (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                        Live
                      </span>
                    ) : (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                        Coming
                      </span>
                    )}
                  </div>
                  {live ? (
                    <>
                      <p className="text-xs text-gray-500">
                        Default{' '}
                        <span className="font-medium text-gray-800">
                          {ch.default_provider
                            ? formatProviderName(String(ch.default_provider))
                            : '—'}
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {(ch.providers ?? []).map(formatProviderName).join(', ') || 'No providers'}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-gray-500">
                      {ch.note || 'Not implemented — no provider adapter yet.'}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Providers</h2>
          <Link
            to={`${DELIVERY_BASE}/providers`}
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
          >
            All providers <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          {loading || (overviewEmpty && providersQ.isLoading) ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : providers.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">No providers registered.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Provider</th>
                    <th className="px-4 py-3 font-medium">Channel</th>
                    <th className="px-4 py-3 font-medium">Capabilities</th>
                    <th className="px-4 py-3 font-medium">Health</th>
                    <th className="px-4 py-3 font-medium">SMS traffic</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {providers.map((p) => {
                    const key = providerKey(p);
                    const count = routedCountryCount(p);
                    return (
                      <tr key={key || formatProviderName(key)} className="hover:bg-gray-50/60">
                        <td className="px-4 py-3">
                          <Link
                            to={`${DELIVERY_BASE}/providers/${encodeURIComponent(key)}`}
                            className="font-medium text-gray-900 hover:text-blue-700"
                          >
                            {formatProviderName(key)}
                          </Link>
                          {p.is_default ? (
                            <span className="ml-2 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                              Default
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {p.channel ? formatChannel(String(p.channel)) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <CapabilityChips caps={p.capabilities ?? []} />
                        </td>
                        <td className="px-4 py-3">
                          <HealthStatus
                            state={resolveHealth(
                              p,
                              healthByProvider.get(key),
                              proxyUnavailable,
                              healthQ.isLoading,
                            )}
                          />
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {count == null
                            ? '—'
                            : count === 0
                              ? 'Registered, no traffic'
                              : `${count} ${count === 1 ? 'country' : 'countries'}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-3">
        <RelatedCard
          href={MESSAGING_TEMPLATES_HREF}
          title="Local templates"
          body="Edit notification_template rows, type, and suppression — messaging surface."
        />
        <RelatedCard
          href={MESSAGING_TYPES_HREF}
          title="Notification types"
          body="Preference toggles and channel allow-flags live on the messaging types tab."
        />
        <RelatedCard
          href="/admin/workflows"
          title="Workflows"
          body="n8n queues and executions that actually dispatch after template lookup."
        />
      </section>
    </DeliveryPageShell>
  );
};

const Stat = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warn' | 'ok';
}) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p
      className={`mt-1 flex items-center gap-1.5 text-2xl font-semibold tabular-nums ${
        tone === 'warn' ? 'text-amber-800' : tone === 'ok' ? 'text-emerald-800' : 'text-gray-900'
      }`}
    >
      {tone === 'warn' ? <AlertTriangle className="h-5 w-5" /> : null}
      {tone === 'ok' ? <CheckCircle2 className="h-5 w-5" /> : null}
      {value}
    </p>
  </div>
);

const RelatedCard = ({ href, title, body }: { href: string; title: string; body: string }) => (
  <Link
    to={href}
    className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-colors duration-150 hover:bg-gray-50"
  >
    <p className="text-sm font-semibold text-gray-900">{title}</p>
    <p className="mt-1 text-xs text-gray-500">{body}</p>
  </Link>
);

export default DeliveryOverviewPage;
